import { Form, Formik } from 'formik';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import * as Yup from 'yup';

// ASSETS
import { EditIcon } from 'assets';

// COMPONENTS
import { PrimaryButton, SVGButton } from 'components/Buttons';
import FlashMessage from 'components/FlashMessage';
import Field, { RateInputField } from 'components/FormField';
import { useModal } from 'components/Modals';
import { StyledCancelButton } from 'components/Modals/styles';

// CONSTANTS
import { FLASH_MESSAGE_TYPES, flashMessageFactory, INITIAL_FLASH_MESSAGE } from 'constants/common';

// HOOKS
import { useAppDispatch } from 'hooks/store';

// STYLES
import { BUTTON_INTENT } from 'styles/buttons';
import colors from 'styles/colors';
import { ModalContent, Modal as ModalDialog, ModalFooter, ModalHeader, Title } from 'styles/modals';
import { CodeDescriptionContainer, TaxNumberRateContainer } from '../styles';

// SERVICES
import { updateTax } from 'services/FleetSettings/thunks';

// TYPES
import { Tax } from 'services/resources';

export const TaxForm = ({
  tax,
  closeModal,
  setFlashMessage,
}: {
  tax: Tax;
  closeModal: (event?: React.SyntheticEvent) => void;
  setFlashMessage: (flashMessage: typeof INITIAL_FLASH_MESSAGE) => void;
}) => {
  const { formatMessage } = useIntl();
  const [submitLoading, setSubmitLoading] = useState(false);
  const dispatch = useAppDispatch();

  const onSubmit = async (values) => {
    setSubmitLoading(true);

    const flashMessage = flashMessageFactory({
      shown: true,
      translationId: 'views.FleetSettings.Charges.flashMessage.editSuccess',
    });

    try {
      await dispatch(updateTax(values));
      setFlashMessage(flashMessage);
    } catch (error) {
      const flashMessage = flashMessageFactory({
        shown: true,
        type: FLASH_MESSAGE_TYPES.error,
        translationId: 'views.FleetSettings.Charges.flashMessage.creationFailed',
      });
      setFlashMessage(flashMessage);
    }
    setSubmitLoading(false);
  };

  const TaxesSchema = Yup.object().shape({
    taxNumber: Yup.number()
      .label(formatMessage({ id: 'views.FleetSettings.Taxes.taxNumber' }))
      .required(),
    rate: Yup.number()
      .label(formatMessage({ id: 'views.FleetSettings.Taxes.rate' }))
      .positive()
      .required(),
    code: Yup.string()
      .label(formatMessage({ id: 'views.FleetSettings.Taxes.code' }))
      .required(),
    description: Yup.string()
      .label(formatMessage({ id: 'views.FleetSettings.Taxes.description' }))
      .max(256),
  });

  const renderActionTitle = () => {
    return <FormattedMessage id="views.FleetSettings.Taxes.actions.editTax" />;
  };

  const initialValues = {
    code: tax.code,
    description: tax.description,
    rate: tax.rate,
    taxNumber: tax.taxNumber,
    id: tax.id,
    seq: tax.seq,
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={TaxesSchema}
      onSubmit={async (values) => {
        await onSubmit(values);
        closeModal();
      }}
    >
      {(formik) => {
        return (
          <>
            <ModalHeader>
              <Title>{renderActionTitle()}</Title>
            </ModalHeader>
            <Form>
              <ModalContent>
                <TaxNumberRateContainer>
                  <Field
                    name="taxNumber"
                    label={formatMessage({ id: 'views.FleetSettings.Taxes.taxNumber' })}
                    required
                  />
                  <Field
                    as={RateInputField}
                    name="rate"
                    label={formatMessage({ id: 'views.FleetSettings.Taxes.rate' })}
                    disabled
                  />
                </TaxNumberRateContainer>

                <CodeDescriptionContainer>
                  <Field name="code" label={formatMessage({ id: 'views.FleetSettings.Taxes.code' })} disabled />
                  <Field
                    name="description"
                    label={formatMessage({ id: 'views.FleetSettings.Taxes.description' })}
                    disabled
                  />
                </CodeDescriptionContainer>
              </ModalContent>
              <ModalFooter>
                <StyledCancelButton onClick={closeModal} />
                <PrimaryButton
                  intent={BUTTON_INTENT.success}
                  type="submit"
                  loading={submitLoading}
                  disabled={!formik.isValid || !formik.dirty}
                >
                  <FormattedMessage id="views.FleetSettings.Taxes.actions.updateTax" />
                </PrimaryButton>
              </ModalFooter>
            </Form>
          </>
        );
      }}
    </Formik>
  );
};

const TaxButton = ({ tax, ...rest }) => {
  const { formatMessage } = useIntl();
  const [flashMessageState, setFlashMessageState] = useState(INITIAL_FLASH_MESSAGE);

  const { openModal, isOpen, Modal, closeModal } = useModal({
    background: colors.modalBackground,
  });

  const onClick: React.MouseEventHandler<HTMLButtonElement> = (event) => {
    openModal(event);
  };

  return (
    <>
      <SVGButton {...rest} onClick={onClick} color={colors.fleetBlue}>
        <EditIcon color={colors.fleetBlue} />
      </SVGButton>

      {flashMessageState.shown && (
        <FlashMessage
          type={flashMessageState.type}
          text={formatMessage({ id: flashMessageState.translationId }, flashMessageState.translationValueObject)}
          onClose={() => {
            setFlashMessageState(INITIAL_FLASH_MESSAGE);
          }}
          visible
        />
      )}
      {isOpen && (
        <Modal>
          <ModalDialog>
            <TaxForm tax={tax} closeModal={closeModal} setFlashMessage={setFlashMessageState} />
          </ModalDialog>
        </Modal>
      )}
    </>
  );
};

export default TaxButton;
